import { apiFetch, ApiError } from '@/services/apiFetch'

export interface AdminUserDto {
  id: string
  username: string
  display_name: string
  status: 'active' | 'disabled'
  must_change_password: boolean
  roles: string[]
  last_login_at: string | null
  created_at: string
  created_by: string | null
  updated_at: string
}

export interface AuthEventDto {
  id: string
  at: string
  username: string
  userId: string | null
  event: string
  ip: string | null
  detail: Record<string, unknown> | null
}

export interface CreateUserPayload {
  username: string
  display_name: string
  password: string
  roles: string[]
}

export interface UpdateUserPayload {
  display_name?: string
  status?: 'active' | 'disabled'
  roles?: string[]
}

export async function listAdminUsers(filter?: { search?: string; status?: string }): Promise<AdminUserDto[]> {
  const params = new URLSearchParams()
  if (filter?.search) params.set('search', filter.search)
  if (filter?.status) params.set('status', filter.status)
  const qs = params.toString()
  const res = await apiFetch(`/api/admin/users${qs ? `?${qs}` : ''}`)
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'LIST_FAILED', json.error?.message ?? 'Không thể tải danh sách người dùng', res.status)
  }
  return json.data
}

export async function createAdminUser(payload: CreateUserPayload): Promise<AdminUserDto> {
  const res = await apiFetch('/api/admin/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'CREATE_FAILED', json.error?.message ?? 'Không thể tạo người dùng', res.status)
  }
  return json.data
}

export async function updateAdminUser(id: string, payload: UpdateUserPayload): Promise<AdminUserDto> {
  const res = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'UPDATE_FAILED', json.error?.message ?? 'Không thể cập nhật người dùng', res.status)
  }
  return json.data
}

export async function resetAdminUserPassword(id: string, newPassword: string): Promise<void> {
  const res = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_password: newPassword }),
  })
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'RESET_FAILED', json.error?.message ?? 'Không thể đặt lại mật khẩu', res.status)
  }
}

export async function revokeAdminUserSessions(id: string): Promise<void> {
  const res = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}/revoke-sessions`, {
    method: 'POST',
  })
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'REVOKE_FAILED', json.error?.message ?? 'Không thể thu hồi phiên', res.status)
  }
}

export async function unlockAdminUser(id: string): Promise<void> {
  const res = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}/unlock`, {
    method: 'POST',
  })
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'UNLOCK_FAILED', json.error?.message ?? 'Không thể mở khóa tài khoản', res.status)
  }
}

export async function listAuthEvents(limit: number = 50): Promise<AuthEventDto[]> {
  const res = await apiFetch(`/api/admin/users/events?limit=${limit}`)
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'EVENTS_FAILED', json.error?.message ?? 'Không thể tải nhật ký xác thực', res.status)
  }
  return json.data
}

export interface RoleDto {
  code: string
  name: string
}

export async function listAdminRoles(): Promise<RoleDto[]> {
  const res = await apiFetch('/api/admin/roles')
  const json = (await res.json()) as any
  if (!res.ok || !json.ok) {
    throw new ApiError(json.error?.code ?? 'LIST_ROLES_FAILED', json.error?.message ?? 'Không thể tải danh sách vai trò', res.status)
  }
  return json.data
}
